// src/components/Shared/ResultsDisplay.tsx
import React, { useState, useEffect } from 'react';
import { FileText, Check, Zap, Shield, Sparkles } from 'lucide-react';
import VisualDiff from './VisualDiff';

interface ReducerResult {
    originalSize: number;
    newSize: number;
    reductionPercentage: number;
    resultId: string;
    fileName: string;
}

interface MergeResult {
    fileName: string;
    size: number;
}

interface SplitResult {
    files?: Array<{ name: string; size: number }>;
    parts?: number;
}

interface ResultsDisplayProps {
    original: File | File[] | null;
    result: any; // Can be ReducerResult, MergeResult, or SplitResult
    type: 'reducer' | 'merge' | 'split';
    noSuccess?: boolean;
    onDownloadSingleFile?: (id: string) => void;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
    original,
    result,
    type,
    noSuccess = false
}) => {
    const [originalUrl, setOriginalUrl] = useState<string | null>(null);

    useEffect(() => {
        let url: string | null = null;
        if (original instanceof File) {
            url = URL.createObjectURL(original);
            setOriginalUrl(url);
        }
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [original]);

    const formatFileSize = (bytes: number | undefined | null): string => {
        if (!bytes && bytes !== 0) return 'Unknown';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const renderReducerResults = () => {
        if (!result) return null;

        const { originalSize, newSize, reductionPercentage, resultId, fileName } = result as ReducerResult;

        // Check if the original is an image or if the filename suggests it
        const isImage = (original instanceof File && original.type.startsWith('image/')) ||
            fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

        // Construct download link via a simpler way or passed prop 
        const compressedUrl = resultId ? `/api/tools/reducer/download/${resultId}` : null;

        return (
            <div className="flex flex-col gap-6">
                {!noSuccess && (
                    <div className="flex items-center gap-4 p-5 bg-blue-50 border border-blue-100 rounded-xl dark:bg-blue-900/20 dark:border-blue-800">
                        <div className="w-11 h-11 bg-white text-emerald-600 rounded-full flex items-center justify-center shadow-sm dark:bg-slate-800 dark:text-emerald-400">
                            <Check size={20} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-lg font-semibold text-slate-800 dark:text-slate-200">Optimization Complete!</span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">Successfully reduced file size while maintaining quality.</span>
                        </div>
                    </div>
                )}

                {isImage && originalUrl && compressedUrl && !noSuccess && (
                    <VisualDiff
                        originalUrl={originalUrl}
                        compressedUrl={compressedUrl}
                        fileName={fileName}
                    />
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl flex items-center gap-3 border border-slate-200 dark:border-slate-700">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white bg-blue-600">
                            <FileText size={18} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-500 uppercase font-semibold dark:text-slate-400">Original</span>
                            <span className="text-[17px] font-bold text-slate-800 dark:text-slate-200">{formatFileSize(originalSize)}</span>
                        </div>
                    </div>

                    <div className="p-4 rounded-xl flex items-center gap-3 border border-slate-200 dark:border-slate-700">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white bg-emerald-600">
                            <Zap size={18} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-500 uppercase font-semibold dark:text-slate-400">Compressed</span>
                            <span className="text-[17px] font-bold text-slate-800 dark:text-slate-200">{formatFileSize(newSize)}</span>
                        </div>
                    </div>

                    <div className="p-4 rounded-xl flex items-center gap-3 border border-slate-200 dark:border-slate-700">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white bg-violet-600">
                            <Sparkles size={18} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-500 uppercase font-semibold dark:text-slate-400">Reduction</span>
                            <span className="text-[17px] font-bold text-slate-800 dark:text-slate-200">{reductionPercentage}%</span>
                        </div>
                    </div>

                    <div className="p-4 rounded-xl flex items-center gap-3 border border-slate-200 dark:border-slate-700">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white bg-amber-600">
                            <Shield size={18} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-slate-500 uppercase font-semibold dark:text-slate-400">Privacy</span>
                            <span className="text-[17px] font-bold text-slate-800 dark:text-slate-200">Verified</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMergeResults = () => {
        if (!result) return null;

        const mergeRes = result as MergeResult;
        const files = Array.isArray(original) ? original : (original ? [original] : []);
        const totalOriginalSize = files.reduce((sum, file) => sum + file.size, 0);

        return (
            <div className="flex flex-col">
                <div className="flex items-center p-3 px-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-600 font-medium mb-6">
                    <Check size={20} className="mr-2" />
                    <span>Files successfully merged!</span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 dark:bg-slate-800 dark:border-slate-700">
                    <div className="flex justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Files Merged:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{files.length}</span>
                    </div>
                    <div className="flex justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Output Format:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{mergeRes.fileName?.split('.').pop()?.toUpperCase() || 'PDF'}</span>
                    </div>
                    <div className="flex justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Original Total Size:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{formatFileSize(totalOriginalSize)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Result Size:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{formatFileSize(mergeRes.size)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderSplitResults = () => {
        if (!result) return null;

        const splitRes = result as SplitResult;
        const partsCount = splitRes.files?.length || splitRes.parts || 0;
        const originalFile = Array.isArray(original) ? original[0] : original;

        return (
            <div className="flex flex-col">
                <div className="flex items-center p-3 px-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-600 font-medium mb-6">
                    <Check size={20} className="mr-2" />
                    <span>File successfully split!</span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 dark:bg-slate-800 dark:border-slate-700">
                    <div className="flex justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Parts Created:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{partsCount}</span>
                    </div>
                    <div className="flex justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <span className="font-medium text-slate-500 dark:text-slate-400">Original Size:</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{formatFileSize(originalFile?.size)}</span>
                    </div>

                    {splitRes.files && splitRes.files.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-base font-semibold text-slate-800 mt-0 mb-4 dark:text-slate-200">File Parts</h4>
                            <ul className="list-none p-0 m-0 max-h-[200px] overflow-y-auto border border-slate-200 rounded-lg dark:border-slate-700">
                                {splitRes.files.map((file, index) => (
                                    <li key={index} className="flex items-center p-3 border-b border-slate-200 last:border-b-0 dark:border-slate-700">
                                        <FileText size={16} className="text-slate-500 mr-3 dark:text-slate-400" />
                                        <span className="flex-1 text-sm text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-200">{file.name}</span>
                                        <span className="text-xs text-slate-500 ml-4 dark:text-slate-400">{formatFileSize(file.size)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="mb-8">
            {type === 'reducer' && renderReducerResults()}
            {type === 'merge' && renderMergeResults()}
            {type === 'split' && renderSplitResults()}
        </div>
    );
};

export default ResultsDisplay;
